const state = {
  variables: {},
  inventory: {},
  quiz_answers: {},
  executedActions: new Set()
}

const mutations = {
  SET_GAME_VARIABLES(state, variables) {
    state.variables = variables
  },
  SET_GAME_VARIABLE(state, { variable, value }) {
    state.variables = {
      ...state.variables,
      [variable]: value
    }
  },
  SET_GAME_INVENTORY(state, inventory) {
    state.inventory = inventory
  },
  ADD_INVENTORY_ITEM(state, { objectId, itemData }) {
    state.inventory = {
      ...state.inventory,
      [objectId]: { ...itemData }
    }
  },
  UPDATE_INVENTORY_ITEM(state, { objectId, itemData }) {
    if (state.inventory[objectId]) {
      state.inventory = {
        ...state.inventory,
        [objectId]: { ...state.inventory[objectId], ...itemData }
      }
    }
  },
  REMOVE_INVENTORY_ITEM(state, objectId) {
    const newInventory = { ...state.inventory }
    delete newInventory[objectId]
    state.inventory = newInventory
  },
  SET_QUIZ_ANSWERS(state, quiz_answers) {
    state.quiz_answers = quiz_answers
  },
  SET_POINT_QUIZ_ANSWERS(state, { pointId, answers }) {
    state.quiz_answers = {
      ...state.quiz_answers,
      [pointId]: { ...answers }
    }
  },
  SET_QUIZ_ANSWER(state, { pointId, questionId, answer }) {
    if (!state.quiz_answers[pointId]) {
      state.quiz_answers[pointId] = {}
    }
    state.quiz_answers = {
      ...state.quiz_answers,
      [pointId]: {
        ...state.quiz_answers[pointId],
        [questionId]: answer
      }
    }
  },
  SET_EXECUTED_ACTIONS(state, executedActions) {
    state.executedActions = new Set(executedActions)
  },
  ADD_EXECUTED_ACTION(state, actionId) {
    state.executedActions.add(actionId)
  }
}

const actions = {
  setGameVariables({ commit }, variables) {
    commit('SET_GAME_VARIABLES', variables)
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameVariables', JSON.stringify(variables))
  },
  setGameVariable({ commit, state }, { variable, value }) {
    commit('SET_GAME_VARIABLE', { variable, value })
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameVariables', JSON.stringify(state.variables))
  },
  setGameInventory({ commit }, inventory) {
    commit('SET_GAME_INVENTORY', inventory)
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameInventory', JSON.stringify(inventory))
  },
  addInventoryItem({ commit, state }, { objectId, itemData }) {
    commit('ADD_INVENTORY_ITEM', { objectId, itemData })
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameInventory', JSON.stringify(state.inventory))
  },
  updateInventoryItem({ commit, state }, { objectId, itemData }) {
    commit('UPDATE_INVENTORY_ITEM', { objectId, itemData })
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameInventory', JSON.stringify(state.inventory))
  },
  removeInventoryItem({ commit, state }, objectId) {
    commit('REMOVE_INVENTORY_ITEM', objectId)
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameInventory', JSON.stringify(state.inventory))
  },
  setQuizAnswers({ commit }, quiz_answers) {
    commit('SET_QUIZ_ANSWERS', quiz_answers)
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameQuizAnswers', JSON.stringify(quiz_answers))
  },
  setPointQuizAnswers({ commit, state }, { pointId, answers }) {
    commit('SET_POINT_QUIZ_ANSWERS', { pointId, answers })
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameQuizAnswers', JSON.stringify(state.quiz_answers))
  },
  setQuizAnswer({ commit, state }, { pointId, questionId, answer }) {
    commit('SET_QUIZ_ANSWER', { pointId, questionId, answer })
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameQuizAnswers', JSON.stringify(state.quiz_answers))
  },
  setExecutedActions({ commit }, executedActions) {
    commit('SET_EXECUTED_ACTIONS', executedActions)
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameExecutedActions', JSON.stringify(Array.from(executedActions)))
  },
  addExecutedAction({ commit, state }, actionId) {
    commit('ADD_EXECUTED_ACTION', actionId)
    // Store in sessionStorage for persistence
    sessionStorage.setItem('gameExecutedActions', JSON.stringify(Array.from(state.executedActions)))
  },
  initializeFromStorage({ commit }) {
    // Load game variables
    const storedVariables = sessionStorage.getItem('gameVariables')
    if (storedVariables) {
      commit('SET_GAME_VARIABLES', JSON.parse(storedVariables))
    }
    
    // Load game inventory
    const storedInventory = sessionStorage.getItem('gameInventory')
    if (storedInventory) {
      commit('SET_GAME_INVENTORY', JSON.parse(storedInventory))
    }
    
    // Load quiz answers
    const storedQuizAnswers = sessionStorage.getItem('gameQuizAnswers')
    if (storedQuizAnswers) {
      commit('SET_QUIZ_ANSWERS', JSON.parse(storedQuizAnswers))
    }
    
    // Load executed actions
    const storedExecutedActions = sessionStorage.getItem('gameExecutedActions')
    if (storedExecutedActions) {
      commit('SET_EXECUTED_ACTIONS', JSON.parse(storedExecutedActions))
    }
  },
  clearGameState({ commit }) {
    // Clear from state
    commit('SET_GAME_VARIABLES', {})
    commit('SET_GAME_INVENTORY', {})
    commit('SET_QUIZ_ANSWERS', {})
    commit('SET_EXECUTED_ACTIONS', new Set())
    
    // Clear from sessionStorage
    sessionStorage.removeItem('gameVariables')
    sessionStorage.removeItem('gameInventory')
    sessionStorage.removeItem('gameQuizAnswers')
    sessionStorage.removeItem('gameExecutedActions')
  }
}

const getters = {
  gameVariables: state => state.variables,
  gameInventory: state => state.inventory,
  gameQuizAnswers: state => state.quiz_answers,
  gameExecutedActions: state => Array.from(state.executedActions),
  getGameVariable: (state) => (variable) => state.variables[variable] || null,
  getInventoryItem: (state) => (objectId) => state.inventory[objectId] || null,
  hasInventoryItem: (state) => (objectId) => !!(state.inventory[objectId] && state.inventory[objectId].quantity > 0),
  getInventoryItemQuantity: (state) => (objectId) => state.inventory[objectId]?.quantity || 0,
  getInventoryCount: (state) => Object.keys(state.inventory).length,
  getTotalInventoryItems: (state) => Object.values(state.inventory).reduce((total, item) => total + (item.quantity || 0), 0),
  getQuizAnswer: (state) => (pointId, questionId) => state.quiz_answers[pointId]?.[questionId] || null,
  getPointQuizAnswers: (state) => (pointId) => state.quiz_answers[pointId] || {},
  hasQuizAnswers: (state) => (pointId) => state.quiz_answers[pointId] && Object.keys(state.quiz_answers[pointId]).length > 0,
  isActionExecuted: (state) => (actionId) => state.executedActions.has(actionId)
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

